import React, { useCallback, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Flex, Text, IconButton, Select, Button, Collapse } from "@chakra-ui/react";
import AnswerMetadataFields from "../components/AnswerMetadataFields";
import { normalizeAnswerMetadata } from "../constants/answerMetadata";
import Editor from "../components/Editor";
import Timer from "../components/Timer";
import SpecialCharacters from "../components/SpecialCharacters";
import Controls from "../components/Controls";
import ToolBar from "../components/ToolBar";
import { FaX } from "react-icons/fa6";
import { shortenWords } from "../utils";
import { fetchAnswer } from "../api";
import {
  ANSWER_TEMPLATE,
  ANSWER_TEMPLATE_OPTIONS,
  getAnswerTemplateLabel,
  normalizeAnswerTemplate,
} from "../constants/answerTemplates";

const Answer = () => {
  const { id } = useParams(); // URL에서 answer id 또는 'new'를 가져옴
  const [answer, setAnswer] = useState(null); // 답안 상태
  // 최초 저장 응답은 ID/메타데이터만 갱신한다. 에디터의 초기 내용은
  // 조회한 답안으로만 바꿔, 저장을 기다리며 추가 작성한 DOM을 보존한다.
  const [editorAnswer, setEditorAnswer] = useState(null);
  const [loading, setLoading] = useState(true); // 로딩 상태
  const [error, setError] = useState(null); // 에러 상태
  const location = useLocation();
  const navigate = useNavigate();
  const { examSubject, timeLimitOption, timeLimit, answerTemplate, answerMetadata } =
    location.state || {}; // 예행 연습에서 넘어온 상태
  const [templateType, setTemplateType] = useState(
    normalizeAnswerTemplate(
      answerTemplate || ANSWER_TEMPLATE.LAW_SCHOOL_COUNCIL,
    ),
  );
  const [selectedChar, setSelectedChar] = useState(""); // 선택된 특수문자
  const [isSpecialCharOpen, setIsSpecialCharOpen] = useState(false); // 특수문자 창 열기/닫기 상태
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false); // 전환 상태
  const editorRef = useRef();
  const textRef = useRef(null);

  const [scale, setScale] = useState(1); // 확대/축소 배율
  const [metadata, setMetadata] = useState(() =>
    normalizeAnswerMetadata(id ? undefined : answerMetadata),
  );
  const [classificationOpen, setClassificationOpen] = useState(false);
  const persistedAnswerId = id || answer?.answer_id || null;
  const editVersion = Boolean(persistedAnswerId);

  const handleAnswerCreated = (createdAnswer) => {
    setAnswer(createdAnswer);
    setTemplateType(normalizeAnswerTemplate(createdAnswer.template_type));
    navigate(`/answers/${createdAnswer.answer_id}`, {
      replace: true,
      state: location.state,
    });
  };

  const handleDraftMetadataLoaded = useCallback(
    (draft) => {
      setMetadata(normalizeAnswerMetadata(draft));
      if (!persistedAnswerId && draft?.template_type) {
        setTemplateType(normalizeAnswerTemplate(draft.template_type));
      }
    },
    [persistedAnswerId],
  );

  // 확대 처리
  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 2)); // 최대 2배 확대
  };

  // 축소 처리
  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5)); // 최소 0.5배 축소
  };

  const handleScaleChange = (nextScale) => {
    setScale(Math.min(Math.max(nextScale, 0.8), 2));
  };

  // 특수문자 창 토글 함수
  const toggleSpecialChar = () => {
    setIsSpecialCharOpen(!isSpecialCharOpen);
  };

  // 특수문자 선택 시 에디터에 반영
  const handleSpecialCharClick = (char) => {
    // 같은 문자를 연속으로 선택해도 매번 삽입되도록 상태를 비운 뒤 갱신한다.
    setSelectedChar("");
    window.requestAnimationFrame(() => setSelectedChar(char));
  };

  const togglePreviewMode = () => {
    setIsTransitioning(true); // 전환 효과 시작
    setTimeout(() => {
      setIsPreviewMode((prevMode) => !prevMode); // 미리보기 모드 전환
      setIsTransitioning(false); // 전환 효과 종료
    }, 300); // 전환 시간을 300ms로 설정
  };
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "변경사항이 저장되지 않을 수 있습니다. 정말로 나가시겠습니까?";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // 현재 페이지를 하나 더 push
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      const confirmed = window.confirm(
        "작성 중인 답안이 사라질 수 있습니다. 정말 나가시겠습니까?",
      );

      if (confirmed) {
        window.removeEventListener("popstate", handlePopState);
        window.history.back();
      } else {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const loadAnswer = async () => {
      if (!id) {
        // New answer case: don't fetch anything, stop loading immediately
        setLoading(false);
        return;
      }

      // 최초 저장 직후에는 POST 응답으로 이미 같은 답안을 가지고 있습니다.
      // 다시 조회해 사용자가 바로 이어 쓴 내용을 덮어쓰지 않도록 합니다.
      if (answer?.answer_id === id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchAnswer(id);
        setAnswer(data); // 응답 데이터로 상태 업데이트
        setEditorAnswer(data);
        setMetadata(normalizeAnswerMetadata(data));
        setTemplateType(normalizeAnswerTemplate(data.template_type));
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false); // 로딩 완료
      }
    };

    loadAnswer();
  }, [id, answer?.answer_id]); // id가 변경될 때마다 다시 실행

  if (loading) {
    return <Text>Loading...</Text>;
  }

  if (error) {
    return <Text>Error: {error}</Text>;
  }

  const isLoggedIn = !!localStorage.getItem("access_token"); // 로그인 여부 확인

  const nickname = isLoggedIn
    ? localStorage.getItem("nickname") || "비회원"
    : "비회원";

  const title =
    nickname === "비회원"
      ? "연습" // 비회원일 때 제목은 "연습"
      : !editVersion
        ? examSubject || "새 답안지"
        : answer?.subject || examSubject || "새 답안지";

  return (
    <Flex direction="column" height="100vh">
      <Flex
        width="full"
        position="fixed"
        top="0"
        zIndex="1000"
        padding={{ base: "8px", md: "20px" }}
        justify="space-between"
        align="center"
        backgroundColor="#003664"
        height="60px"
      >
        <Flex gap="10px" alignItems="center" display={{ base: "none", md: "flex" }}>
          <Text color="gold">닉네임</Text>
          <Text color="white" fontSize="24px">
            {nickname}
          </Text>
        </Flex>
        <Flex direction="column" align="center">
          <Text fontSize={{ base: "14px", md: "20px" }} color="white">
            {shortenWords(title)}
          </Text>
          <Text fontSize="11px" color="blue.100">
            {getAnswerTemplateLabel(templateType)}
          </Text>
        </Flex>
        <Timer timeLimitOption={timeLimitOption || "none"} timeLimit={timeLimit} />
      </Flex>

        <Box
          position="fixed"
          top="108px"
          right={{ base: "8px", md: "16px" }}
          zIndex="1100"
          bg="white"
          borderWidth="1px"
          borderColor="blue.200"
          borderRadius="md"
          boxShadow="md"
          p="2"
          width={classificationOpen ? "min(340px, calc(100vw - 16px))" : "auto"}
          maxH="calc(100dvh - 180px)"
          overflowY="auto"
        >
          <Button size="xs" variant="ghost" onClick={() => setClassificationOpen(!classificationOpen)} aria-expanded={classificationOpen}>
            과목·기출정보 {classificationOpen ? "접기" : "설정"}
          </Button>
          <Collapse in={classificationOpen}>
            <Box mt="2"><AnswerMetadataFields value={metadata} onChange={setMetadata} /></Box>
          </Collapse>
          {!persistedAnswerId && <>
          <Text fontSize="xs" fontWeight="bold" color="gray.600" mb="1">
            답안지 양식
          </Text>
          <Select
            size="sm"
            value={templateType}
            onChange={(event) => setTemplateType(event.target.value)}
            aria-label="답안지 양식"
          >
            {ANSWER_TEMPLATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          </>}
        </Box>

      <Flex
        // flex="1" // 본문이 남은 공간을 모두 차지하도록 설정
        mb="40px" // 하단 바 높이만큼 여백 추가
        justify="center"
        backgroundColor="gray.100"
        overflow="auto" // 본문이 넘칠 경우 스크롤 가능하도록 설정
        height="calc(100vh - 60px)"
      >
        {/* 본문 */}
        <ToolBar
          toggleSpecialChar={toggleSpecialChar}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onScaleChange={handleScaleChange}
          scale={scale}
          templateType={templateType}
          isPreviewMode={isPreviewMode}
          isTransitioning={isTransitioning}
        />

        {/* 특수문자 팝업 (툴바 오른쪽) */}
        {isSpecialCharOpen && (
          <Box
            width={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "208px"
                : "200px"
            }
            maxHeight={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "410px"
                : undefined
            }
            overflowY={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "auto"
                : "visible"
            }
            position="fixed"
            left={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "auto"
                : "100px"
            }
            right={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "8px"
                : "auto"
            }
            top={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "100px"
                : "170px"
            }
            bg="white"
            p={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE ? "4px" : "4"
            }
            border={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "0"
                : "1px solid gray"
            }
            boxShadow={
              templateType === ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE
                ? "none"
                : "md"
            }
            zIndex="1001"
          >
            {templateType !== ANSWER_TEMPLATE.MINISTRY_OF_JUSTICE && (
              <>
                <IconButton
                  aria-label="Close"
                  icon={<FaX />}
                  onClick={() => setIsSpecialCharOpen(false)}
                  position="absolute"
                  top="4px"
                  right="4px"
                  size="sm"
                  variant="ghost"
                />
                <Box height="20px" />
              </>
            )}
            <SpecialCharacters
              onCharacterClick={handleSpecialCharClick}
              templateType={templateType}
            />
          </Box>
        )}

        <Editor
          ref={editorRef}
          specialChar={selectedChar}
          scale={scale}
          isPreviewMode={isPreviewMode}
          isTransitioning={isTransitioning}
          editorRef={textRef}
          fetchedContent={editorAnswer?.content || ""}
          fetchedContentHtml={editorAnswer?.content_html || ""}
          templateType={templateType}
        />

        {/* 하단 버튼 */}
      </Flex>
      <Flex
        justify="flex-end"
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="#A2C2C0"
        p="2"
        flexWrap="wrap"
        gap="1"
        borderTop="1px solid gray"
        // zIndex="1000"
      >
        <Controls
          togglePreviewMode={togglePreviewMode}
          isPreviewMode={isPreviewMode}
          textRef={editorRef}
          editorRef={textRef}
          subject={title}
          editVersion={editVersion}
          answer_id={persistedAnswerId}
          onAnswerCreated={handleAnswerCreated}
          templateType={templateType}
          metadata={metadata}
          initialDuration={editorAnswer?.duration_seconds}
          onDraftMetadataLoaded={handleDraftMetadataLoaded}
        />
      </Flex>
    </Flex>
  );
};

export default Answer;
